import { Component, ViewChild, signal, computed } from "@angular/core";
import { IonContent, NavParams } from "@ionic/angular";
import { ChatProvider } from "src/providers/chat.provider";
import { TabsProvider } from "src/providers/tabs/tabs.provider";
import { TranslatorProvider } from "src/providers/translator.provider";
import { ChatItemModel, ChatItemStyle, ChatItemType } from "src/shared/models/chat/chat-item.model";
import { ChatType } from "src/shared/models/chat/chat.model";
import { parseUtcDate } from "src/shared/utils/date-utils";

@Component({
  selector: 'rota-age-chat',
  styleUrl: 'chat.page.scss',
  templateUrl: 'chat.page.html',
  standalone: false,
})
export class ChatPage {
  @ViewChild(IonContent) chatContainer: IonContent;

  id = signal<string>('');
  chatTitle = signal<string>('');
  type = signal<ChatType | null>(null);
  items = signal<Array<ChatItemModel>>([]);

  isAnimatedLastMessage = signal<boolean>(false);
  countChatUpdate = signal<number>(0);

  private readonly cornerClasses = {
    single: 'single-corner',
    start: 'start-corner',
    middle: 'middle-corner',
    end: 'end-corner',
  }

  get locale() {
    return this.translator.currentLanguage;
  }

  constructor(
    private params: NavParams,
    private tabsProvider: TabsProvider,
    private chatService: ChatProvider,
    private translator: TranslatorProvider,
  ) { }

  async goBack() {
    await this.tabsProvider.forwardBack();
  }

  get chatType(): typeof ChatType {
    return ChatType;
  }

  get chatItemType(): typeof ChatItemType {
    return ChatItemType;
  }

  async ionViewWillEnter() {
    this.id.set(this.params.get(ChatProvider.MODEL.ID));
    this.chatTitle.set(this.params.get(ChatProvider.MODEL.TITLE));
    this.type.set(this.params.get(ChatProvider.MODEL.TYPE));

    this.chatService.items$.subscribe(items => {
      this.items.set(this.populateDateDelimiter(items));

      if (this.countChatUpdate() === 1) this.isAnimatedLastMessage.set(true);
      this.countChatUpdate.update(count => count + 1);

      setTimeout(async () => {
        await this.chatContainer.scrollToBottom(1000);
      }, 1000);
    });
  }

  corners = computed(() => {
    const items = this.items();
    const corners: string[] = [];
    
    for (let currIndex = 0; currIndex < items.length; currIndex++) {
      const prevIndex = currIndex - 1;
      const nextIndex = currIndex + 1;
      const isEqualClient = (index1: number, index2: number) => {
        return items[index1].fromClient === items[index2].fromClient;
      }
      const isNotEqualClient = (index1: number, index2: number) => {
        return items[index1].fromClient !== items[index2].fromClient;
      }
      const isEqualDay = (index1: number, index2: number) => {
        const date1 = parseUtcDate(items[index1].createdAt);
        const date2 = parseUtcDate(items[index2].createdAt);

        return date1.getDate() === date2.getDate()
          && date1.getMonth() === date2.getMonth()
          && date1.getFullYear() === date2.getFullYear();
      }

      if (nextIndex === items.length && currIndex === 0) {
        corners.push(this.cornerClasses.single);
        continue;
      }

      if (currIndex === 0) {
        if (isEqualClient(currIndex, nextIndex)) {
          if (isEqualDay(currIndex, nextIndex)) {
            corners.push(this.cornerClasses.start);
            continue;
          }
          corners.push(this.cornerClasses.single);
          continue;
        }
        corners.push(this.cornerClasses.single);
        continue;
      }

      if (nextIndex === items.length) {
        if (isEqualClient(currIndex, prevIndex)) {
          if (isEqualDay(currIndex, prevIndex)) {
            corners.push(this.cornerClasses.end);
            continue;
          }
          corners.push(this.cornerClasses.single);
          continue;
        }
        corners.push(this.cornerClasses.single);
        continue;
      }

      if (isNotEqualClient(currIndex, prevIndex) && isNotEqualClient(currIndex, nextIndex)) {
        corners.push(this.cornerClasses.single);
        continue;
      }

      if (isNotEqualClient(currIndex, prevIndex)) {
        if (isEqualDay(currIndex, nextIndex)) {
          corners.push(this.cornerClasses.start);
          continue;
        }
        corners.push(this.cornerClasses.single);
        continue;
      }

      if (isNotEqualClient(currIndex, nextIndex)) {
        if (isEqualDay(currIndex, prevIndex)) {
          corners.push(this.cornerClasses.end);
          continue;
        }
        corners.push(this.cornerClasses.single);
        continue;
      }

      if (isEqualDay(currIndex, nextIndex)) {
        if (isEqualDay(currIndex, prevIndex)) {
          corners.push(this.cornerClasses.middle);
          continue;
        }
        corners.push(this.cornerClasses.start);
        continue;
      }

      corners.push(this.cornerClasses.end);
    }
    
    return corners;
  });

  getCorner(currIndex: number): string {
    return this.corners()[currIndex] || this.cornerClasses.single;
  }

  getTitleColor(style?: ChatItemStyle) {
    if (style === undefined) return '';
    return ChatItemStyle[style].toLowerCase();
  }

  isOffset(currIndex: number): boolean {
    return !this.items()[currIndex].dateDelimiter;
  }

  populateDateDelimiter(items: Array<ChatItemModel>): Array<ChatItemModel> {
    for (let index = 0; index < items.length; index++) {
      const currElement = items[index];

      if (index == 0) {
        currElement.dateDelimiter = true;
        continue;
      }

      const currDate = parseUtcDate(currElement.createdAt);
      const prevDate = parseUtcDate(items[index - 1].createdAt);

      currElement.dateDelimiter = index > 0 && (
        currDate.getDate() !== prevDate.getDate() ||
        currDate.getMonth() !== prevDate.getMonth() ||
        currDate.getFullYear() !== prevDate.getFullYear()
      );
    }

    return items;
  }
}
